import React, { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import LightModeIcon from "@mui/icons-material/LightMode";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HomeIcon from "@mui/icons-material/Home";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import CodeIcon from "@mui/icons-material/Code";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ArticleIcon from "@mui/icons-material/Article";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import StorageIcon from "@mui/icons-material/Storage";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

const drawerWidth = 260;

function Navigation({ parentToChild, modeChange }: any) {
  const { t } = useTranslation();

  const navItems = [
    { label: t("Accueil"),         path: "/",                icon: <HomeIcon fontSize="small" /> },
    { label: t("Expériences"),     path: "/timeline",         icon: <WorkHistoryIcon fontSize="small" /> },
    { label: t("Compétences"),     path: "/expertise",        icon: <CodeIcon fontSize="small" /> },
    { label: t("Projects"),        path: "/projects",         icon: <RocketLaunchIcon fontSize="small" /> },
    { label: t("Blog"),            path: "/blog",             icon: <ArticleIcon fontSize="small" /> },
    { label: t("certificationsTitle"), path: "/certifications", icon: <WorkspacePremiumIcon fontSize="small" /> },
    { label: "CMDB",               path: "/cmdb",             icon: <StorageIcon fontSize="small" /> },
    { label: t("contactTitle"),    path: "/contact",          icon: <AlternateEmailIcon fontSize="small" /> },
  ];

  const { mode } = parentToChild;
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ height: "100%" }}>
      <div className="nav-drawer-header">Menu</div>
      <Divider sx={{ borderColor: "var(--border)" }} />
      <List sx={{ pt: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding className="nav-drawer-item">
            <ListItemButton component={Link} to={item.path}>
              <ListItemIcon sx={{ minWidth: 36, color: "var(--accent)" }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        component="nav"
        id="navigation"
        className={`nav-appbar${scrolled ? " scrolled" : ""}`}
        position="fixed"
      >
        <Toolbar className="nav-toolbar">
          <Link to="/" className="nav-logo">
            <HomeIcon fontSize="small" />
            <span>Accueil</span>
          </Link>

          <Box className="nav-links" sx={{ display: { xs: "none", sm: "flex" } }}>
            {navItems.slice(1).map((item) => (
              <Button
                key={item.label}
                component={Link}
                to={item.path}
                className={`nav-link-btn${location.pathname === item.path ? " active" : ""}`}
                startIcon={item.icon}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          <Box className="nav-controls">
            <LanguageSwitcher />
            <IconButton className="nav-icon-btn" onClick={modeChange} size="small">
              {mode === "dark" ? (
                <LightModeIcon fontSize="small" />
              ) : (
                <DarkModeIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton
              className="nav-icon-btn"
              onClick={handleDrawerToggle}
              size="small"
              sx={{ display: { sm: "none" } }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              backgroundColor: "var(--bg-surface)",
              borderRight: "1px solid var(--border)",
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}

export default Navigation;
